import React, { useState, useEffect } from 'react';
import { useGeneration } from 'contexts/GenerationContext';
import { Card, Button, Progress } from 'components/ui';
import { BsStars, BsX, BsArrowRight } from 'react-icons/bs';
import { motion, AnimatePresence } from 'framer-motion';
import { TbAlertCircle, TbCheck, TbLoader2 } from 'react-icons/tb';

const GenerationProgressIndicator = () => {
    const {
        activeGeneration,
        hasActiveGeneration,
        getGenerationProgress,
        navigateToActiveGeneration,
        cancelGeneration,
        clearGeneration
    } = useGeneration();
    
    const [isExpanded, setIsExpanded] = useState(false);
    const [shouldShow, setShouldShow] = useState(false);
    
    const progress = getGenerationProgress();
    const isActive = hasActiveGeneration();
    
    // Show/hide logic
    useEffect(() => {
        if (isActive || (activeGeneration && !activeGeneration.isLoading)) {
            setShouldShow(true);
        } else {
            const timer = setTimeout(() => setShouldShow(false), 300);
            return () => clearTimeout(timer);
        }
    }, [isActive, activeGeneration]);
    
    // Auto-collapse when generation completes
    useEffect(() => {
        if (activeGeneration && !activeGeneration.isLoading && isExpanded) {
            const timer = setTimeout(() => setIsExpanded(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [activeGeneration?.isLoading, isExpanded]);
    
    const handleNavigate = () => {
        navigateToActiveGeneration();
        setIsExpanded(false);
    };
    
    const handleCancel = (e) => {
        e.stopPropagation();
        cancelGeneration();
        setIsExpanded(false);
    };
    
    const handleClear = (e) => {
        e.stopPropagation();
        clearGeneration();
        setIsExpanded(false);
    };
    
    const getStatusIcon = () => {
        if (activeGeneration?.cancelled) {
            return <TbAlertCircle className="w-3 h-3 text-white" />;
        }
        if (activeGeneration && !activeGeneration.isLoading) {
            return <TbCheck className="w-3 h-3 text-white" />;
        }
        return <TbLoader2 className="w-3 h-3 animate-spin text-white" />;
    };
    
    const getStatusText = () => {
        if (activeGeneration?.cancelled) {
            return 'Generation Cancelled';
        }
        if (activeGeneration && !activeGeneration.isLoading) {
            return `Generated ${activeGeneration.posts.length} posts`;
        }
        return 'Generating posts...';
    };
    
    const getTemplateTitle = (template) => {
        const templateMap = {
            'suggest': 'AI Suggestions',
            'prompt': 'From Prompt',
            'chat': 'From Chat',
            'images': 'From Images',
            'news': 'From News',
            'holidays': 'From Holidays',
            'google': 'From Google',
            'brainstorm': 'From Brainstorm',
            'questions': 'From Questions',
            'raw': 'From Raw Text',
            'url': 'From URLs'
        };
        return templateMap[template] || template;
    };
    
    if (!shouldShow) return null;
    
    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 20 }}
                className="fixed bottom-6 right-6 z-50"
            >
                <Card
                    className={`
                        cursor-pointer transition-all duration-300 shadow-lg hover:shadow-xl !overflow-hidden
                        ${isExpanded ? 'w-80' : 'w-16 h-16'}
                        ${activeGeneration?.cancelled ? 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950' : ''}
                        ${activeGeneration && !activeGeneration.isLoading && !activeGeneration.cancelled ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950' : ''}
                    `}
                    onClick={() => !isExpanded && setIsExpanded(true)}
                    bodyClass='!p-0 !overflow-hidden'
                >
                    <AnimatePresence mode="wait">
                        {!isExpanded ? (
                            // Collapsed state - just the icon
                            <motion.div
                                key="collapsed"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="w-full h-full flex items-center justify-center relative"
                            >
                                <div className="text-2xl">
                                    {activeGeneration?.cancelled ? (
                                        <TbAlertCircle className="text-orange-500" />
                                    ) : activeGeneration && !activeGeneration.isLoading ? (
                                        <TbCheck className="text-green-500" />
                                    ) : (
                                        <TbLoader2 className="animate-spin text-primary-500" />
                                    )}
                                </div>
                                
                                {/* Progress ring for active generations */}
                                {/* {isActive && (
                                    <div className="absolute inset-0 -m-1">
                                        <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                                            <path
                                                className="stroke-gray-200 dark:stroke-gray-700"
                                                strokeWidth="2"
                                                fill="none"
                                                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                            />
                                            <path
                                                className="stroke-primary-500"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                fill="none"
                                                strokeDasharray={`${progress.percentage}, 100`}
                                                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                            />
                                        </svg>
                                    </div>
                                )} */}
                            </motion.div>
                        ) : (
                            // Expanded state - full details with modern design
                            <motion.div
                                key="expanded"
                                initial={{ opacity: 0, scaleY: 0 }}
                                animate={{ opacity: 1, scaleY: 1 }}
                                exit={{ opacity: 0, scaleY: 0 }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                                className="relative overflow-hidden origin-top"
                            >
                                {/* Header Section with Gradient Background */}
                                <div className="relative px-4 pt-4 pb-3 bg-gradient-to-br from-slate-50/80 to-blue-50/60 dark:from-slate-800/80 dark:to-slate-900/60 border-b border-gray-200/50 dark:border-gray-700/50">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-1.5 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 shadow-sm">
                                                <BsStars className="w-3.5 h-3.5 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="!text-sm font-bold text-gray-800 dark:text-gray-200">
                                                    AI Generation
                                                </h3>
                                                {activeGeneration?.template && (
                                                    <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">
                                                        {getTemplateTitle(activeGeneration.template)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsExpanded(false);
                                            }}
                                            className="p-1.5 rounded-lg bg-white/60 dark:bg-gray-700/60 hover:bg-white dark:hover:bg-gray-700 border border-gray-200/50 dark:border-gray-600/50 transition-all duration-200 hover:shadow-sm group"
                                            title="Collapse"
                                        >
                                            <BsX className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors" />
                                        </button>
                                    </div>
                                </div>

                                {/* Status & Progress Section */}
                                <div className="p-4 space-y-3 bg-gradient-to-b from-white/40 to-gray-50/40 dark:from-gray-800/40 dark:to-gray-900/40">
                                    {/* Status Card */}
                                    <div className={`relative p-3 rounded-xl border backdrop-blur-sm transition-all duration-300 ${
                                        activeGeneration?.cancelled 
                                            ? 'bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-200/30 dark:border-orange-700/30' 
                                            : activeGeneration && !activeGeneration.isLoading 
                                            ? 'bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-200/30 dark:border-emerald-700/30' 
                                            : 'bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border-blue-200/30 dark:border-blue-700/30'
                                    }`}>
                                        <div className='flex items-center gap-3'>
                                            <div className={`p-1 rounded-lg shadow-sm ${
                                                activeGeneration?.cancelled 
                                                    ? 'bg-gradient-to-br from-orange-400 to-red-500' 
                                                    : activeGeneration && !activeGeneration.isLoading 
                                                    ? 'bg-gradient-to-br from-emerald-400 to-teal-500' 
                                                    : 'bg-gradient-to-br from-blue-400 to-indigo-500'
                                            }`}>
                                                {getStatusIcon()}
                                            </div>
                                            <div className="flex-1">
                                                <span className={`text-xs font-semibold ${
                                                    activeGeneration?.cancelled 
                                                        ? 'text-orange-700 dark:text-orange-300' 
                                                        : activeGeneration && !activeGeneration.isLoading 
                                                        ? 'text-emerald-700 dark:text-emerald-300' 
                                                        : 'text-blue-700 dark:text-blue-300'
                                                }`}>
                                                    {getStatusText()}
                                                </span>
                                                {activeGeneration && (
                                                    <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                                                        {activeGeneration.completedAt 
                                                            ? `Completed ${new Date(activeGeneration.completedAt).toLocaleTimeString()}`
                                                            : activeGeneration.cancelledAt
                                                            ? `Cancelled ${new Date(activeGeneration.cancelledAt).toLocaleTimeString()}`
                                                            : `Started ${new Date(activeGeneration.startedAt).toLocaleTimeString()}`
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Progress Bar for Active Generations */}
                                    {isActive && (
                                        <div className="relative p-3 rounded-xl border bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border-blue-200/30 dark:border-blue-700/30 backdrop-blur-sm">
                                            <div className='flex items-center justify-between mb-2'>
                                                <span className='text-xs font-semibold text-blue-700 dark:text-blue-300'>Progress</span>
                                                <span className='text-xs font-bold text-gray-800 dark:text-gray-200'>
                                                    {progress.current} 
                                                    <span className="text-gray-400 dark:text-gray-500 font-normal text-[10px]"> / {progress.total}</span>
                                                </span>
                                            </div>
                                            
                                            <div className="relative h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full">
                                                <div 
                                                    className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full transition-all duration-500 ease-out shadow-sm"
                                                    style={{ width: `${Math.min(progress.percentage, 100)}%` }}
                                                />
                                            </div>
                                            
                                            <div className="mt-2 text-center">
                                                <span className="text-[10px] text-blue-600 dark:text-blue-400 font-medium">
                                                    {progress.percentage}% Complete
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Action Buttons Footer */}
                                <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-blue-50/50 dark:from-gray-800 dark:to-gray-900/50 border-t border-gray-200/30 dark:border-gray-700/30">
                                    <div className="flex gap-2">
                                        {isActive ? (
                                            <>
                                                <Button
                                                    size="sm"
                                                    variant="solid"
                                                    onClick={handleNavigate}
                                                    className="flex-1 !text-xs !px-3 !py-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 border-0 shadow-md hover:shadow-lg transition-all duration-200"
                                                    icon={<BsArrowRight className="w-3 h-3" />}
                                                >
                                                    View Progress
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="plain"
                                                    onClick={handleCancel}
                                                    className="!text-xs !px-3 !py-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all duration-200"
                                                >
                                                    Cancel
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button
                                                    size="sm"
                                                    variant="solid"
                                                    onClick={handleNavigate}
                                                    className="flex-1 !text-xs !px-3 !py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 border-0 shadow-md hover:shadow-lg transition-all duration-200"
                                                    icon={<BsArrowRight className="w-3 h-3" />}
                                                >
                                                    {activeGeneration?.cancelled ? 'Try Again' : 'View Results'}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="plain"
                                                    onClick={handleClear}
                                                    className="!text-xs !px-3 !py-2 text-gray-500 hover:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all duration-200"
                                                >
                                                    <BsX className="w-3 h-3" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Card>
            </motion.div>
        </AnimatePresence>
    );
};

export default GenerationProgressIndicator;
